import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // undefined = vérification en cours, null = non connecté, objet = connecté
  const [user, setUserState] = useState(undefined);
  const checkEpoch = useRef(0);
  const checkController = useRef(null);
  const retryTimer = useRef(null);
  const invalidateChecks = useCallback(() => {
    checkEpoch.current += 1;
    clearTimeout(retryTimer.current);
    checkController.current?.abort();
    checkController.current = null;
  }, []);
  const setUser = useCallback((data) => {
    invalidateChecks();
    setUserState(data);
    try {
      if (data) localStorage.setItem("bc_user_cache", JSON.stringify(data));
      else localStorage.removeItem("bc_user_cache");
    } catch { /* Storage can be unavailable; server cookies remain authoritative. */ }
  }, [invalidateChecks]);

  const checkAuth = useCallback(async () => {
    invalidateChecks();
    const epoch = checkEpoch.current;
    const controller = new AbortController();
    checkController.current = controller;
    try {
      const { data } = await api.get("/auth/me", { signal: controller.signal });
      if (epoch !== checkEpoch.current) return null;
      setUserState(data);
      try { localStorage.setItem("bc_user_cache", JSON.stringify(data)); } catch {}
      return data;
    } catch (e) {
      if (controller.signal.aborted || epoch !== checkEpoch.current) return null;
      const status = e?.response?.status;
      if (!status) {
        // Pas de réponse (réseau coupé au lancement de l'app écran d'accueil) : on garde la session connue
        // au lieu d'afficher l'écran de connexion, et on réessaie.
        let cached = null;
        try { cached = JSON.parse(localStorage.getItem("bc_user_cache") || "null"); } catch {}
        if (cached) {
          setUserState(cached);
          retryTimer.current = setTimeout(() => { if (epoch === checkEpoch.current) checkAuth(); }, 4000);
          return cached;
        }
      }
      if (status === 401 || status === 403) { try { localStorage.removeItem("bc_user_cache"); } catch {} }
      setUserState(null);
      return null;
    } finally {
      if (epoch === checkEpoch.current) checkController.current = null;
    }
  }, [invalidateChecks]);

  useEffect(() => {
    // CRITICAL : si on revient du callback OAuth, AuthCallback gère l'échange d'abord
    if (window.location.hash?.includes("session_id=")) return;
    checkAuth();
    return invalidateChecks;
  }, [checkAuth, invalidateChecks]);

  const login = async (email, password) => {
    invalidateChecks();
    const { data } = await api.post("/auth/login", { email, password }, { timeout: 15000 });
    setUser(data);
    try { localStorage.setItem("bc_user_cache", JSON.stringify(data)); } catch {}
    return data;
  };

  const register = async (name, email, password, refCode = null, cgvAccepted = false, newsletter = true) => {
    invalidateChecks();
    const payload = { name, email, password, cgv_accepted: cgvAccepted, newsletter };
    if (refCode) payload.ref_code = refCode;
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // IMPORTANT: pas "/studio" — en prod l'hébergeur statique résout /studio vers le fichier
    // studio.html qui avale le fragment #session_id. /dashboard n'a pas d'équivalent statique.
    let redirectUrl = window.location.origin + "/dashboard";
    try {
      const ref = sessionStorage.getItem("beatcut_ref");
      if (ref) redirectUrl += `?ref=${encodeURIComponent(ref)}`;
    } catch {}
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const startAnonymous = async () => {
    // Session sans compte (« Commencer maintenant ») : cookie serveur, quotas FREE, liée au compte au 1er téléchargement
    invalidateChecks();
    const { data } = await api.post("/auth/anonymous", {}, { timeout: 15000 });
    setUser(data);
    return data;
  };

  const logout = async () => {
    invalidateChecks();
    await api.post("/auth/logout");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, loginWithGoogle, startAnonymous, logout, refreshUser: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
